import FooterComponent from "@/components/common/layout/footer";
import Loader from "@/components/common/ui/loader";
import Modal from "@/components/common/ui/modal";
import { useOrgAndSiteContext } from "@/context";
import { IBreadcrumb, useBreadcrumbContext } from "@/context/breadcrumb";
import { useVisitedSitesWithoutCommonScript } from "@/hooks/store";
import {
  IDefaultLayoutV2Props,
  LayoutNextPage,
  V2_DEFAULT_LAYOUT_TYPE,
} from "@/types/layout";
import { getLocaleFromPathname } from "@/utils/i18n";
import { SITE_INTEGRATION_TYPE } from "@shared/common/constant/site";
import { deleteCookie, getCookie } from "cookies-next";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import { NextPage } from "next";
import { useSession } from "next-auth/react";
import { NextRouter, useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import PaymentAlert from "../common/payment-alert";
import Cafe24OnboardingModal from "../team/cafe24-onboarding-modal";
import GodomallOnboardingModal from "../team/godomall-onboarding-modal";
import MakeshopOnboardingModal from "../team/makeshop-onboarding-modal";
import NhnOnboardingModal from "../team/nhn-onboarding-modal";
import OnboardingModal from "../team/onboarding-modal";
import OnboardingScript from "../team/onboarding/onboarding-script";
import SixshopProOnboardingModal from "../team/sixshop-pro-onboarding-modal";
import WordpressOnboardingModal from "../team/wordpress-onboarding-modal";
import DefaultHeader from "./default-header";
import Sidebar from "./sidebar";

interface IDefaultLayoutProps {
  Page: LayoutNextPage;
  layoutType: V2_DEFAULT_LAYOUT_TYPE;
  options?: {
    flexClass?: string;
    getBreadcrumbs?: (router: NextRouter) => IBreadcrumb[] | null;
  };
}

// dayjs 언어 설정
const nextLocaleCookie = getCookie("NEXT_LOCALE");
const pathname = typeof window !== "undefined" ? window.location.pathname : "";
dayjs.locale(
  nextLocaleCookie === "jp"
    ? "ja"
    : nextLocaleCookie
    ? nextLocaleCookie
    : getLocaleFromPathname(pathname)
);

const DefaultLayout: React.FC<IDefaultLayoutProps> = (props) => {
  const [collapsed, setCollapsed] = useState(false);

  const {
    currentOrganization,
    currentSite,
    organizations,
    currentOrFallbackSites: sites,
    isSitesFetching,
  } = useOrgAndSiteContext();

  const { addVisitedSite, sites: visitedSitesWithoutCommonScript } =
    useVisitedSitesWithoutCommonScript();
  const { data: sessionData } = useSession();

  const [onboardingModalState, setOnboardingModalState] = useState<
    | null
    | "normal"
    | "cafe24"
    | "nhn"
    | "makeshop"
    | "godomall"
    | "wordpress"
    | "sixshop_pro"
    | "common_script"
  >(null);

  const { Page, layoutType } = props;
  const PageDefaultLayoutV2 = Page as NextPage<IDefaultLayoutV2Props>;
  const PageWithoutOrganization = Page as NextPage;

  const router = useRouter();

  useEffect(() => {
    const locale = getCookie("NEXT_LOCALE");
    if (locale && locale !== router.locale) {
      const { pathname, asPath, query } = router;
      router.replace({ pathname, query }, asPath, { locale });
    }
  }, [router]);

  const { setBreadcrumbs } = useBreadcrumbContext();
  useEffect(() => {
    setBreadcrumbs(props.options?.getBreadcrumbs?.(router) || null);
  }, [setBreadcrumbs, props.options, router]);

  useEffect(() => {
    if (isSitesFetching) {
      setOnboardingModalState(null);
      return;
    }

    // 사이트가 없는 경우
    if (!sites || sites.length === 0) {
      setOnboardingModalState("normal");
    }

    // 사이트가 있는데 온보딩이 3~4단계라면
    else if (
      sites &&
      sites.length > 0 &&
      (currentOrganization?.onboardStep === 10 ||
        currentOrganization?.onboardStep === 30 ||
        currentOrganization?.onboardStep === 40)
    ) {
      setOnboardingModalState("normal");
    }

    // CAFE24 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.CAFE24
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      setOnboardingModalState("cafe24");
    }

    // NHN 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.NHN
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      setOnboardingModalState("nhn");
    }

    // makeshop 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.MAKESHOP
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      setOnboardingModalState("makeshop");
    }

    // godomall 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.GODOMALL
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      setOnboardingModalState("godomall");
    }

    // wordpress 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.WORDPRESS
      ) &&
      (currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70)
    ) {
      setOnboardingModalState("wordpress");
    }

    // sixshop_pro 앱으로 접근하고 온보딩이 끝나지 않은 경우
    else if (
      sites &&
      sites.length > 0 &&
      currentSite?.siteIntegration?.find(
        (integration) => integration.type === SITE_INTEGRATION_TYPE.SIXSHOP_PRO
      )
    ) {
      if (
        currentOrganization?.onboardStep === 50 ||
        currentOrganization?.onboardStep === 60 ||
        currentOrganization?.onboardStep === 70
      ) {
        setOnboardingModalState("sixshop_pro");
      }

      if (
        router.asPath !== "/v2/signup/email" &&
        router.asPath !== "/v2/welcome" &&
        !router.asPath.includes("/v2/auth/signup-email")
      ) {
        deleteCookie("cnb_i_token");
        deleteCookie("cnb_i_type");
      }
    }

    // 온보딩 이후에도 공통 스크립트 설치를 하지 않았을 때 안내 (하루 내에 중복으로 노출되지 않음)
    else if (
      currentSite &&
      currentOrganization?.onboardStep === 100 &&
      !currentSite?.isInstalled
    ) {
      const targetSite = visitedSitesWithoutCommonScript.find(
        (site) => site.publicId === currentSite.publicId
      );

      if (!targetSite) {
        setOnboardingModalState("common_script");
      } else if (targetSite.expiresAt < new Date().getTime()) {
        setOnboardingModalState("common_script");
      } else {
        setOnboardingModalState(null);
      }
    }

    // 그 외
    else {
      setOnboardingModalState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrganization?.onboardStep,
    currentSite,
    isSitesFetching,
    visitedSitesWithoutCommonScript,
  ]);

  const handleFinishAddSite = useCallback(async () => {
    setOnboardingModalState(null);
    if (currentSite) {
      addVisitedSite(currentSite.publicId);
    }
  }, [currentSite, addVisitedSite]);

  return (
    <section
      id="contentSection"
      className={twMerge(
        "v2override",
        layoutType !== "LOGO" && "min-w-[1280px]",
        layoutType === V2_DEFAULT_LAYOUT_TYPE.HEADER && "layout-header"
      )}
    >
      {layoutType === "LOGO" ? (
        <DefaultHeader
          headerType="logo"
          collapsed={collapsed}
          setCollapsed={() => setCollapsed(!collapsed)}
        />
      ) : (
        <></>
      )}
      {layoutType !== "LOGO" ? (
        <section className={twMerge("flex w-full", props.options?.flexClass)}>
          {layoutType !== "HEADER" && (
            <Sidebar
              collapsed={collapsed}
              currentOrganization={currentOrganization || undefined}
            />
          )}

          <div className="min-w-0 grow">
            {/* 대시보드에서만 해당 위치에 배너가 보여야됨. BETA 기간동안 제공         
              TODO: 베타 종료 시 해당 주석 해제*/}
            {/* {router.pathname === "/v2/[org_slug]/[site_public_id]" && (
              <div
                className="sticky top-0 z-40 flex items-center justify-center gap-2 cursor-pointer text-cb-gray-50 h-14 bg-cb-gray-900"
                onClick={() => {
                  router.replace({
                    pathname: `/v2/[org_slug]/[site_public_id]/dashboard`,
                    query: {
                      org_slug: currentOrganization?.slug,
                      site_public_id: currentSite?.publicId,
                    },
                  });
                }}
                data-gtm-id="dashboard-top-banner"
              >
                <span className="text-lg font-semibold">
                  완전히 새로워진 대시보드를 확인해 보세요
                </span>
                <ArrowRight className="w-4 h-4" />
              </div>
            )} */}
            <DefaultHeader
              headerType="default"
              collapsed={collapsed}
              setCollapsed={() => setCollapsed(!collapsed)}
            />
            {/* 결제알림 알럿 */}
            {currentOrganization && (
              <PaymentAlert currentOrganization={currentOrganization} />
            )}
            <div
              className={twMerge(
                "pb-6 pl-6 pr-6 text-base content-wrapper",
                layoutType === "HEADER" && "bg-cb-gray-50 pl-0 pr-0" // 회색 배경 영역
              )}
              style={{
                minHeight: "calc(100vh - var(--header-height))",
              }}
            >
              {/* Default Layout 안에 있는 페이지들은 `[org_slug]` 가 있으면 무조건
              `state.currentOrganization` 이 존재하고, `[site_public_id]` 가
              있으면 무조건 `state.currentSite` 가 있음을 가정하고 있습니다.
              이것을 가질려면 로그인을 한 상태가 되어야 하기 때문에
              `sessionData`이 있어야 렌더링되도록 합니다. 만약 로그인 하지
              않아도 제대로 렌더링되어야 한다면, `useCurrentOrgAndSite`에서
              가상의 `currentOrganization`, `currentSite` 만들어 내려주고
              sessionData 조건을 삭제하면 됩니다. */}
              {sessionData ? <PageDefaultLayoutV2 /> : <Loader size="large" />}
            </div>
          </div>
        </section>
      ) : (
        <div className="px-5 pb-20 mt-16 md:mt-28 md:px-0 md:pb-28">
          <div className="flex flex-col items-center justify-center text-base">
            <PageWithoutOrganization />
          </div>
        </div>
      )}
      {layoutType === "DEFAULT" &&
      organizations &&
      currentOrganization &&
      !isSitesFetching ? (
        <>
          <OnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "normal"}
          />
          <Cafe24OnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "cafe24"}
          />
          <NhnOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "nhn"}
          />
          <MakeshopOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "makeshop"}
          />
          <GodomallOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "godomall"}
          />
          <WordpressOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "wordpress"}
          />
          <SixshopProOnboardingModal
            organizations={organizations}
            currentOrganization={currentOrganization}
            currentSite={currentSite}
            onComplete={handleFinishAddSite}
            showOnboarding={onboardingModalState === "sixshop_pro"}
          />
          <Modal
            title="공통 스크립트 설치"
            open={onboardingModalState === "common_script"}
            hideFooter
            hideCloseButton
          >
            <OnboardingScript
              prefixGtmId="dashboard"
              publicId={currentSite?.publicId || ""}
              siteType={currentSite?.siteType}
              initialSiteUrl={currentSite?.url}
              onFinish={() => {
                if (currentSite) {
                  addVisitedSite(currentSite.publicId);
                }
                setOnboardingModalState(null);
              }}
            />
          </Modal>
        </>
      ) : (
        <></>
      )}
      {process.env.NEXT_PUBLIC_PAYMENT_APP && (
        <div className="pt-[100px]">
          <FooterComponent />
        </div>
      )}
    </section>
  );
};

export const getDefaultLayout = (
  layoutType: V2_DEFAULT_LAYOUT_TYPE,
  options?: {
    flexClass?: string;
    getBreadcrumbs?: (router: NextRouter) => IBreadcrumb[] | null;
  }
) => {
  const siteLayout = (Page: LayoutNextPage, props: Record<string, unknown>) => (
    <DefaultLayout
      layoutType={layoutType}
      options={options}
      {...props}
      Page={Page}
    />
  );

  return siteLayout;
};
